import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, RotateCcw, Globe, MapPin, Users, ShieldAlert, ShieldCheck,
  TrendingUp, Activity, Smartphone, ExternalLink, Calendar, Filter,
  AlertTriangle,
} from 'lucide-react';
import MigrationReport from './MigrationReport';
import LoginDiagnosticsPanel from './LoginDiagnosticsPanel';

import { ALL_COUNTRIES } from '../lib/countries';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Shows a country flag emoji from an ISO-2 country code. */
const flag = (code) => {
  if (!code || code.length !== 2) return '';
  const A = 0x1F1E6;
  return String.fromCodePoint(A + code.toUpperCase().charCodeAt(0) - 65)
       + String.fromCodePoint(A + code.toUpperCase().charCodeAt(1) - 65);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return iso; }
};

const ipMapUrl = (ip) => `https://www.iplocation.net/ip-lookup?query=${encodeURIComponent(ip)}`;

// Tiny KPI tile — used for the top stats row.
// When `onClick` is provided, the whole tile becomes a button that drives
// the events table filter below. Hover lift + cursor make affordance obvious.
const Kpi = ({ icon: Icon, label, value, sub, accent = 'cyan', testid, onClick, active = false }) => {
  const accents = {
    cyan:    'from-cyan-500 to-blue-600',
    violet:  'from-violet-500 to-fuchsia-600',
    emerald: 'from-emerald-500 to-teal-600',
    amber:   'from-amber-500 to-orange-600',
    rose:    'from-rose-500 to-red-600',
    slate:   'from-slate-500 to-slate-700',
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      data-testid={testid}
      onClick={onClick}
      className={`text-left bg-white rounded-xl p-3 sm:p-4 border shadow-sm transition-all w-full ${
        onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-slate-300 active:scale-[0.98]' : ''
      } ${active ? 'border-cyan-400 ring-2 ring-cyan-200' : 'border-slate-200'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">{label}</p>
          <p className="text-xl sm:text-2xl font-extrabold text-slate-800 mt-1 font-mono tabular-nums leading-none">{value}</p>
          {sub && <p className="text-[11px] text-slate-500 mt-1.5 truncate">{sub}</p>}
        </div>
        <div className={`w-9 h-9 flex-shrink-0 rounded-lg bg-gradient-to-br ${accents[accent]} text-white flex items-center justify-center shadow`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Tag>
  );
};

// SVG bar chart — data: [{ label, count, success, failed }]. Stacked.
const BucketChart = ({ data }) => {
  const max = useMemo(() => Math.max(1, ...data.map((b) => b.count || 0)), [data]);
  const W = 720, H = 200, pad = 24;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const bw = data.length > 0 ? Math.max(2, innerW / data.length - 2) : 0;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[200px]" data-testid="login-buckets-chart">
      <defs>
        <linearGradient id="grSuccess" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#2563eb" />
        </linearGradient>
        <linearGradient id="grFailed" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fb923c" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
      </defs>
      {/* y grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
        <line key={i} x1={pad} x2={W - pad} y1={pad + innerH * (1 - p)} y2={pad + innerH * (1 - p)} stroke="#e2e8f0" strokeWidth="1" strokeDasharray={p === 0 ? 0 : '2 2'} />
      ))}
      {data.map((b, i) => {
        const total = b.count || 0;
        const totH = (total / max) * innerH;
        const failH = ((b.failed || 0) / max) * innerH;
        const x = pad + i * (innerW / Math.max(data.length, 1)) + 1;
        const y = pad + innerH - totH;
        return (
          <g key={i}>
            <title>{`${b.label}: ${total} logins (${b.success || 0} success / ${b.failed || 0} failed)`}</title>
            <rect x={x} y={y} width={bw} height={totH - failH} fill="url(#grSuccess)" rx="2" />
            <rect x={x} y={pad + innerH - failH} width={bw} height={failH} fill="url(#grFailed)" rx="2" />
          </g>
        );
      })}
      {/* x labels — show every Nth depending on density */}
      {data.map((b, i) => {
        const step = data.length > 24 ? Math.ceil(data.length / 12) : (data.length > 12 ? 2 : 1);
        if (i % step !== 0) return null;
        const x = pad + i * (innerW / Math.max(data.length, 1)) + bw / 2 + 1;
        // For monthly buckets ("YYYY-MM") show the short month name, not the
        // last 5 chars (which collide visually for adjacent years).
        let lbl = b.label;
        if (/^\d{4}-\d{2}$/.test(lbl)) {
          const [, mm] = lbl.split('-');
          lbl = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(mm, 10) - 1] || lbl;
        } else if (lbl.length > 7) {
          lbl = lbl.slice(-5);
        }
        return <text key={i} x={x} y={H - 6} fontSize="10" textAnchor="middle" fill="#64748b">{lbl}</text>;
      })}
    </svg>
  );
};

// Donut for device split.
const DeviceDonut = ({ data }) => {
  const total = data.reduce((s, d) => s + (d.count || 0), 0) || 1;
  const colors = { desktop: '#2563eb', mobile: '#10b981', tablet: '#f59e0b', bot: '#dc2626', unknown: '#94a3b8' };
  let cum = 0;
  const r = 50, c = 60, stroke = 18;
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 120 120" className="w-[120px] h-[120px] -rotate-90" data-testid="device-donut">
        <circle cx={c} cy={c} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        {data.map((d, i) => {
          const frac = (d.count || 0) / total;
          const dasharray = `${frac * 2 * Math.PI * r} ${2 * Math.PI * r}`;
          const dashoffset = -cum * 2 * Math.PI * r;
          cum += frac;
          return (
            <circle key={i} cx={c} cy={c} r={r} fill="none"
              stroke={colors[d.device_type] || '#94a3b8'}
              strokeWidth={stroke}
              strokeDasharray={dasharray}
              strokeDashoffset={dashoffset}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
      <div className="flex flex-col gap-1.5">
        {data.map((d) => (
          <div key={d.device_type} className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 rounded" style={{ background: colors[d.device_type] || '#94a3b8' }} />
            <span className="capitalize text-slate-700 w-16">{d.device_type}</span>
            <span className="font-mono font-bold text-slate-800 tabular-nums">{d.count}</span>
            <span className="text-slate-500">({((d.count / total) * 100).toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ManageIPPanel({ token }) {
  // Which sub-view of the IP page is active. "activity" = the classic
  // Manage IP + Login Activity dashboard. "errors" = the diagnostic view
  // for 500-during-login incidents (previously a top-level admin tab —
  // moved here so the primary nav stays uncluttered).
  const [subTab, setSubTab] = useState('activity');
  const [range, setRange] = useState('week');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  // Per-language usage breakdown (Phase H8, msg-1045). Lives next to the
  // Device split card so admin can quickly compare "which locales are
  // actually being used" against the active translation effort.
  const [langStats, setLangStats] = useState(null);

  // Filters for the events table
  const [f, setF] = useState({
    email: '', name: '', account_id: '', ip: '', country: '',
    success: 'all', device_type: 'all', date_start: '', date_end: '',
  });
  const [events, setEvents] = useState({ items: [], total: 0, success_count: 0, failed_count: 0, unique_ip_count: 0 });
  const [eventsLoading, setEventsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [r, lr] = await Promise.all([
        fetch(`${API_URL}/api/admin/login-stats?range=${range}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/login-language-stats?range=${range}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(r.ok ? await r.json() : null);
      setLangStats(lr.ok ? await lr.json() : null);
    } catch (_) { setStats(null); setLangStats(null); }
    finally { setStatsLoading(false); }
  }, [range, token]);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
    Object.entries(f).forEach(([k, v]) => {
      if (v && v !== 'all') params.append(k, v);
    });
    try {
      const r = await fetch(`${API_URL}/api/admin/login-events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(r.ok ? await r.json() : { items: [], total: 0, success_count: 0, failed_count: 0, unique_ip_count: 0 });
    } catch (_) {
      setEvents({ items: [], total: 0, success_count: 0, failed_count: 0, unique_ip_count: 0 });
    }
    finally { setEventsLoading(false); }
  }, [f, page, token]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const resetFilters = () => {
    setF({ email: '', name: '', account_id: '', ip: '', country: '', success: 'all', device_type: 'all', date_start: '', date_end: '' });
    setPage(0);
  };

  // Quick-filter shortcut: applies a date-range (and optional success flag)
  // and scrolls the events table into view. Used by the KPI tiles so a
  // click jumps the user straight to the matching list — no manual date
  // pickers needed.
  const applyQuickRange = useCallback((preset, extra = {}) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    let start = '', end = '';
    if (preset === 'today') {
      start = end = fmt(today);
    } else if (preset === 'yesterday') {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      start = end = fmt(y);
    } else if (preset === 'week') {
      const dow = (today.getDay() + 6) % 7; // Mon=0..Sun=6
      const ms = new Date(today); ms.setDate(today.getDate() - dow);
      start = fmt(ms); end = fmt(today);
    } else if (preset === 'month') {
      const ms = new Date(today.getFullYear(), today.getMonth(), 1);
      start = fmt(ms); end = fmt(today);
    } else if (preset === 'year') {
      start = `${today.getFullYear()}-01-01`; end = fmt(today);
    } else if (preset === 'all') {
      start = ''; end = '';
    }
    setF((p) => ({ ...p, date_start: start, date_end: end, success: extra.success ?? p.success, device_type: extra.device_type ?? p.device_type }));
    setPage(0);
    // Smooth-scroll to the table so the quick-filter has visible feedback.
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="events-table-anchor"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Detect which preset (if any) the current filter matches — used to
  // highlight the corresponding KPI tile with a cyan ring so the admin
  // sees what's active right now.
  const activePreset = useMemo(() => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const t = fmt(today);
    if (!f.date_start && !f.date_end) return f.success === 'false' ? 'failed' : null;
    if (f.date_start === t && f.date_end === t) return f.success === 'true' ? 'today-success' : 'today';
    const dow = (today.getDay() + 6) % 7;
    const ms = new Date(today); ms.setDate(today.getDate() - dow);
    if (f.date_start === fmt(ms) && f.date_end === t) return 'week';
    if (f.date_start === `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-01` && f.date_end === t) return 'month';
    return null;
  }, [f.date_start, f.date_end, f.success]);

  const k = stats?.kpis || {};

  return (
    <div className="w-full max-w-[1500px] mx-auto" data-testid="manage-ip-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2.5">
            <Globe className="w-7 h-7 text-cyan-600" />
            Manage IP & Login Activity
          </h1>
          <p className="text-sm text-slate-500 mt-1">Track every login attempt — IPs, countries, devices, and security events.</p>
        </div>
        {/* Range pills */}
        <div className="inline-flex bg-slate-100 rounded-lg p-1" data-testid="range-pills">
          {['today', 'week', 'month', 'year', 'all'].map((r) => (
            <button
              key={r}
              data-testid={`range-${r}`}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${
                range === r ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >{r}</button>
          ))}
        </div>
      </div>

      {/* Sub-tabs — "Login Errors" (formerly a top-level admin nav item)
         was moved here to keep the primary admin navigation from
         overflowing on narrow screens. Both surfaces stay one click
         away from the same landing page. */}
      <div className="flex items-center gap-2 mb-5 border-b border-slate-200" data-testid="manage-ip-subtabs">
        <button
          type="button"
          data-testid="manage-ip-subtab-activity"
          onClick={() => setSubTab('activity')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            subTab === 'activity'
              ? 'border-cyan-600 text-cyan-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Activity className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Login Activity
        </button>
        <button
          type="button"
          data-testid="manage-ip-subtab-errors"
          onClick={() => setSubTab('errors')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            subTab === 'errors'
              ? 'border-amber-500 text-amber-700'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <AlertTriangle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Login Errors
        </button>
      </div>

      {subTab === 'errors' ? (
        <LoginDiagnosticsPanel token={token} />
      ) : (
        <React.Fragment>
      {/* ── Migration Cutover Report ───────────────────────────────────
          Pinned to the top of the page so it's the first thing the admin
          sees during a site migration window. Independent of the range
          pills above — has its own window selector (1h / 6h / 24h / 7d /
          30d / custom) tuned for cutover work. */}
      <MigrationReport token={token} />

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
        <Kpi testid="kpi-today"     icon={Activity}    label="Logins today"    value={statsLoading ? '…' : k.today ?? 0}     sub={`Yesterday: ${k.yesterday ?? 0}`} accent="cyan"
             onClick={() => applyQuickRange('today')} active={activePreset === 'today'} />
        <Kpi testid="kpi-week"      icon={TrendingUp}  label="This week"       value={statsLoading ? '…' : k.this_week ?? 0} accent="violet"
             onClick={() => applyQuickRange('week')}  active={activePreset === 'week'} />
        <Kpi testid="kpi-month"     icon={Calendar}    label="This month"      value={statsLoading ? '…' : k.this_month ?? 0} accent="emerald"
             onClick={() => applyQuickRange('month')} active={activePreset === 'month'} />
        <Kpi testid="kpi-total"     icon={ShieldCheck} label="All time"        value={statsLoading ? '…' : k.total_all_time ?? 0} accent="slate"
             onClick={() => applyQuickRange('all')}   active={!activePreset && f.success === 'all'} />
        <Kpi testid="kpi-uips"      icon={Globe}       label="Unique IPs today" value={statsLoading ? '…' : k.unique_ips_today ?? 0} accent="amber"
             onClick={() => applyQuickRange('today')} />
        <Kpi testid="kpi-uusers"    icon={Users}       label="Active users"    value={statsLoading ? '…' : k.unique_users_today ?? 0} sub="Today" accent="cyan"
             onClick={() => applyQuickRange('today', { success: 'true' })} active={activePreset === 'today-success'} />
        <Kpi testid="kpi-success"   icon={ShieldCheck} label="Success rate"    value={statsLoading ? '…' : `${(((k.success_rate ?? 1)) * 100).toFixed(1)}%`} sub={`${k.window_success ?? 0}/${k.window_total ?? 0}`} accent="emerald" />
        <Kpi testid="kpi-failed"    icon={ShieldAlert} label="Failed (window)" value={statsLoading ? '…' : (k.window_total ?? 0) - (k.window_success ?? 0)} accent="rose"
             onClick={() => applyQuickRange('all', { success: 'false' })} active={activePreset === 'failed'} />
      </div>

      {/* Top countries TODAY — hero panel always pinned to "today" so admin
          gets at-a-glance "where are people logging in from right now",
          independent of the range selector. Uses a horizontal-bar layout
          with flag, name, count and percent for instant readability. */}
      {(stats?.top_countries_today?.length || 0) > 0 && (
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200 shadow-sm mb-5" data-testid="top-countries-today">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-600" /> Top countries today
              <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full text-[11px] font-bold">
                {stats.top_countries_today.reduce((s, c) => s + c.count, 0)} logins
              </span>
            </h3>
            <span className="text-[11px] text-slate-500">
              {stats.top_countries_today.length} countr{stats.top_countries_today.length === 1 ? 'y' : 'ies'}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {stats.top_countries_today.slice(0, 5).map((c, idx) => {
              const totalToday = stats.top_countries_today.reduce((s, x) => s + x.count, 0) || 1;
              const pct = (c.count / totalToday) * 100;
              const grad = ['from-cyan-500 to-blue-600','from-violet-500 to-fuchsia-600','from-emerald-500 to-teal-600','from-amber-500 to-orange-600','from-rose-500 to-red-600'][idx % 5];
              return (
                <button
                  key={c.code + c.name}
                  onClick={() => { setF((p) => ({ ...p, country: c.code || '' })); setPage(0); }}
                  data-testid={`today-country-${c.code || 'na'}`}
                  className="group relative p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-md text-left transition-all overflow-hidden bg-gradient-to-br from-slate-50 to-white"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-3xl flex-shrink-0 leading-none">{flag(c.code)}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-800 truncate">{c.name}</p>
                      <p className="text-[11px] text-slate-500">#{idx + 1} today</p>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1.5 mt-2.5">
                    <span className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none">{c.count}</span>
                    <span className="text-[11px] font-semibold text-slate-500">logins · {pct.toFixed(0)}%</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${grad} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
          {stats.top_countries_today.length > 5 && (
            <p className="text-[11px] text-slate-500 mt-3">
              +{stats.top_countries_today.length - 5} more countries today — see full list below.
            </p>
          )}
        </div>
      )}

      {/* Bucket chart + device donut + reasons */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="lg:col-span-2 bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-800 text-sm">Login activity ({range})</h3>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gradient-to-b from-cyan-500 to-blue-600" />Success</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gradient-to-b from-orange-400 to-red-600" />Failed</span>
            </div>
          </div>
          {stats?.buckets?.length ? (
            <BucketChart data={stats.buckets} />
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No data</div>
          )}
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2"><Smartphone className="w-4 h-4 text-violet-600" /> Device split</h3>
          {stats?.device_split?.length ? <DeviceDonut data={stats.device_split} /> : <p className="text-xs text-slate-400">No data</p>}
          <h4 className="font-bold text-slate-700 text-xs mt-4 mb-2 uppercase tracking-wider flex items-center gap-1.5"><ShieldAlert className="w-3 h-3 text-rose-500" /> Failure reasons</h4>
          <div className="space-y-1">
            {(stats?.reason_split || []).slice(0, 5).map((rr) => (
              <div key={rr.reason} className="flex justify-between text-xs">
                <span className="text-slate-600 capitalize">{(rr.reason || 'unknown').replaceAll('_', ' ')}</span>
                <span className="font-mono font-bold text-rose-600 tabular-nums">{rr.count}</span>
              </div>
            ))}
            {(!stats?.reason_split || stats.reason_split.length === 0) && (
              <p className="text-xs text-slate-400">No failed attempts in this window. Healthy 🎯</p>
            )}
          </div>
          {/* Language usage breakdown — driven by /admin/login-language-stats.
              Bars are scaled to the busiest locale so admin can eyeball
              "is the Italian effort paying off?" at a glance. Numbers
              show DISTINCT users in the window, not raw login counts,
              because a single noisy user otherwise dominates the bar.
              Phase H8 (msg-1045). */}
          <h4 className="font-bold text-slate-700 text-xs mt-4 mb-2 uppercase tracking-wider flex items-center gap-1.5" data-testid="lang-usage-card">
            <Globe className="w-3 h-3 text-cyan-500" /> Language usage ({langStats?.total_users ?? 0} users)
          </h4>
          <div className="space-y-1.5" data-testid="lang-usage-list">
            {(langStats?.rows || []).length === 0 ? (
              <p className="text-xs text-slate-400">No login data in this window.</p>
            ) : (() => {
              const maxUsers = Math.max(1, ...(langStats?.rows || []).map((r) => r.users));
              return (langStats?.rows || []).map((row) => (
                <div key={row.language} className="flex items-center gap-2 text-xs" data-testid={`lang-row-${row.language}`}>
                  <span className="font-mono font-bold uppercase text-slate-700 w-12 flex-shrink-0">{row.language || 'en'}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all"
                      style={{ width: `${(row.users / maxUsers) * 100}%` }}
                    />
                  </div>
                  <span className="font-mono font-bold text-cyan-700 tabular-nums w-16 text-right flex-shrink-0">{row.users}</span>
                  <span className="text-slate-400 tabular-nums w-12 text-right flex-shrink-0">{row.users_pct}%</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Top countries / IPs / users */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-600" /> Top countries</h3>
          <div className="space-y-2">
            {(stats?.top_countries || []).map((c) => (
              <button
                key={c.code + c.name}
                onClick={() => { setF((p) => ({ ...p, country: c.code || c.name })); setPage(0); }}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-left"
                data-testid={`top-country-${c.code || 'na'}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-lg">{flag(c.code)}</span>
                  <span className="text-sm text-slate-700 truncate">{c.name}</span>
                </span>
                <span className="font-mono text-sm font-bold text-slate-800 tabular-nums">{c.count}</span>
              </button>
            ))}
            {(!stats?.top_countries || stats.top_countries.length === 0) && <p className="text-xs text-slate-400">No data</p>}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-600" /> Top IP addresses</h3>
          <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
            {(stats?.top_ips || []).map((ip) => (
              <div key={ip.ip} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <a href={ipMapUrl(ip.ip)} target="_blank" rel="noreferrer" className="font-mono text-xs font-bold text-cyan-700 hover:underline">{ip.ip}</a>
                    {ip.country_code && <span className="text-sm">{flag(ip.country_code)}</span>}
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{ip.city || ip.country || ''} · {ip.last_user_email || '—'}</p>
                </div>
                <span className="font-mono text-sm font-bold text-slate-800 tabular-nums">{ip.count}</span>
              </div>
            ))}
            {(!stats?.top_ips || stats.top_ips.length === 0) && <p className="text-xs text-slate-400">No data</p>}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-violet-600" /> Top users</h3>
          <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
            {(stats?.top_users || []).map((u) => (
              <button
                key={u.user_id}
                onClick={() => { setF((p) => ({ ...p, email: u.user_email || '' })); setPage(0); }}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-left"
                data-testid={`top-user-${u.user_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-slate-700 truncate font-medium">
                    {u.first_name || ''} {u.surname || ''}
                    {u.account_id && <span className="ml-1 text-[11px] font-mono text-slate-400">#{u.account_id}</span>}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">{u.user_email}</p>
                </div>
                <div className="text-right">
                  <span className="font-mono text-sm font-bold text-slate-800 tabular-nums">{u.logins}</span>
                  <p className="text-[10px] text-slate-400">{u.distinct_ips} IPs</p>
                </div>
              </button>
            ))}
            {(!stats?.top_users || stats.top_users.length === 0) && <p className="text-xs text-slate-400">No data</p>}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm mb-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2"><Filter className="w-4 h-4 text-slate-600" /> Filters</h3>
          <button onClick={resetFilters} className="text-xs px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded font-semibold flex items-center gap-1.5" data-testid="reset-filters">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          <input data-testid="filter-email"   value={f.email}      onChange={(e) => { setF({ ...f, email: e.target.value }); setPage(0); }} placeholder="Email" className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500" />
          <input data-testid="filter-name"    value={f.name}       onChange={(e) => { setF({ ...f, name: e.target.value }); setPage(0); }} placeholder="Name (first or last)" className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500" />
          <input data-testid="filter-account" value={f.account_id} onChange={(e) => { setF({ ...f, account_id: e.target.value }); setPage(0); }} placeholder="Account #" className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500" />
          <input data-testid="filter-ip"      value={f.ip}         onChange={(e) => { setF({ ...f, ip: e.target.value }); setPage(0); }} placeholder="IP address" className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono" />
          <select data-testid="filter-country" value={f.country}    onChange={(e) => { setF({ ...f, country: e.target.value }); setPage(0); }} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white">
            <option value="">All countries</option>
            {/* Smart pinning — countries that actually appear in your logs
                (via stats.top_countries) are listed first with a count, so
                you see your real footprint without scrolling 250 entries. */}
            {(() => {
              const seen = new Map();
              (stats?.top_countries || []).forEach((c) => { if (c.code) seen.set(c.code, c.count); });
              (stats?.top_countries_today || []).forEach((c) => { if (c.code && !seen.has(c.code)) seen.set(c.code, c.count); });
              const usedCodes = [...seen.keys()];
              const used = ALL_COUNTRIES
                .filter((c) => seen.has(c.code))
                .sort((a, b) => (seen.get(b.code) || 0) - (seen.get(a.code) || 0));
              const rest = ALL_COUNTRIES.filter((c) => !usedCodes.includes(c.code));
              return (
                <>
                  {used.length > 0 && (
                    <optgroup label={`In your logs (${used.length})`}>
                      {used.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.name} · {seen.get(c.code)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="All countries">
                    {rest.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                    ))}
                  </optgroup>
                </>
              );
            })()}
          </select>
          <select data-testid="filter-success" value={f.success}    onChange={(e) => { setF({ ...f, success: e.target.value }); setPage(0); }} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500">
            <option value="all">All attempts</option>
            <option value="true">Success only</option>
            <option value="false">Failed only</option>
          </select>
          <select data-testid="filter-device" value={f.device_type} onChange={(e) => { setF({ ...f, device_type: e.target.value }); setPage(0); }} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500">
            <option value="all">All devices</option>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
            <option value="bot">Bot</option>
            <option value="unknown">Unknown</option>
          </select>
          <input data-testid="filter-date-start" type="date" value={f.date_start} onChange={(e) => { setF({ ...f, date_start: e.target.value }); setPage(0); }} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500" />
          <input data-testid="filter-date-end"   type="date" value={f.date_end}   onChange={(e) => { setF({ ...f, date_end: e.target.value }); setPage(0); }} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500" />
        </div>
      </div>

      {/* Quick date-range chips — fast keyboard-free filtering for the
          events table below. The KPI tiles already do this for today/
          week/month/all; this strip adds Year and Yesterday. */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="quick-range-strip">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 mr-1">Quick filter:</span>
        {[
          { id: 'today',     label: 'Today' },
          { id: 'yesterday', label: 'Yesterday' },
          { id: 'week',      label: 'This week' },
          { id: 'month',     label: 'This month' },
          { id: 'year',      label: 'This year' },
          { id: 'all',       label: 'All time' },
        ].map((p) => {
          const isActive = (
            (p.id === 'today'     && activePreset === 'today') ||
            (p.id === 'week'      && activePreset === 'week') ||
            (p.id === 'month'     && activePreset === 'month') ||
            (p.id === 'all'       && !activePreset && f.success === 'all') ||
            (p.id === 'year'      && f.date_start === `${new Date().getFullYear()}-01-01` && f.date_end === new Date().toISOString().slice(0,10))
          );
          return (
            <button
              key={p.id}
              data-testid={`quick-${p.id}`}
              onClick={() => applyQuickRange(p.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Filter result badges */}
      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        <span className="px-2.5 py-1 bg-slate-100 rounded-full font-bold text-slate-700">
          <Search className="w-3 h-3 inline mr-1" /> {events.total} matches
        </span>
        <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full font-bold">{events.success_count} success</span>
        <span className="px-2.5 py-1 bg-rose-100 text-rose-700 rounded-full font-bold">{events.failed_count} failed</span>
        <span className="px-2.5 py-1 bg-cyan-100 text-cyan-700 rounded-full font-bold">{events.unique_ip_count} unique IPs</span>
      </div>

      {/* Events table — desktop */}
      <div data-testid="events-table-anchor" className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap">When</th>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap">Account</th>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap">User</th>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap">IP</th>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap">Location</th>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap">Device</th>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap" title="UI language at login. ⚓ = user's saved preference, 🌐 = browser Accept-Language fallback.">Lang</th>
                <th className="px-2.5 py-2 text-left font-bold text-slate-600 uppercase tracking-wider text-[11px] whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {eventsLoading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : events.items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No login events match your filters.</td></tr>
              ) : events.items.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`event-row-${e.id}`}>
                  <td className="px-2.5 py-2 text-xs text-slate-600 font-mono whitespace-nowrap">{fmtDate(e.created_at)}</td>
                  <td className="px-2.5 py-2 font-mono text-xs whitespace-nowrap">{e.account_id ? <span className="text-slate-700">#{e.account_id}</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-2.5 py-2 max-w-[220px]">
                    <div className="text-slate-800 font-medium truncate" title={[e.first_name, e.surname].filter(Boolean).join(' ') || ''}>{[e.first_name, e.surname].filter(Boolean).join(' ') || '—'}</div>
                    <div className="text-[11px] text-slate-500 truncate" title={e.user_email || ''}>{e.user_email || '—'}</div>
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap">
                    <a href={ipMapUrl(e.ip)} target="_blank" rel="noreferrer" className="font-mono text-xs text-cyan-700 hover:underline inline-flex items-center gap-1" data-testid={`ip-link-${e.id}`}>
                      {e.ip || '—'}{e.ip && <ExternalLink className="w-3 h-3 opacity-60" />}
                    </a>
                  </td>
                  <td className="px-2.5 py-2 text-xs max-w-[200px] truncate" title={`${e.city ? e.city + ', ' : ''}${e.country || ''}`}>
                    {e.country_code && <span className="mr-1">{flag(e.country_code)}</span>}
                    <span className="text-slate-700">{e.city ? `${e.city}, ` : ''}{e.country || '—'}</span>
                  </td>
                  <td className="px-2.5 py-2 text-xs text-slate-600 capitalize whitespace-nowrap max-w-[180px] truncate" title={`${e.device_type || ''}${e.browser ? ' · ' + e.browser : ''}`}>
                    <span className="capitalize">{e.device_type || 'unknown'}</span>
                    {e.browser && <span className="text-slate-400"> · {e.browser}</span>}
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap" data-testid={`lang-${e.id}`}>
                    {/* `language_source` distinguishes a deliberate user
                        preference (anchor icon) from a browser-only hint
                        (globe icon). Falls back to a plain dash when
                        nothing was captured — older login_events rows
                        predating Phase H8 simply don't carry the field.
                        Tooltip shows the raw Accept-Language string so
                        admin can tell, e.g., `it-IT,it;q=0.9` from
                        `en-US` even when both got bucketed into `it`. */}
                    {e.language ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                          e.language_source === 'user_preference'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                        title={`${e.language_source === 'user_preference' ? 'Saved profile preference' : 'Browser Accept-Language fallback'}${e.accept_language ? ` · raw: ${e.accept_language}` : ''}`}
                      >
                        <span className="text-[9px] opacity-70">{e.language_source === 'user_preference' ? '⚓' : '🌐'}</span>
                        {e.language}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap">
                    {e.success ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[11px] font-bold"><ShieldCheck className="w-3 h-3" /> OK</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[11px] font-bold" title={e.reason || ''}><ShieldAlert className="w-3 h-3" /> {(e.reason || 'failed').replaceAll('_', ' ')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {events.total > PAGE_SIZE && (
          <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs">
            <span className="text-slate-600">Page {page + 1} of {Math.ceil(events.total / PAGE_SIZE)}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40">← Prev</button>
              <button onClick={() => setPage(page + 1)} disabled={(page + 1) * PAGE_SIZE >= events.total} className="px-3 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40">Next →</button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile cards — stacked layout */}
      <div className="md:hidden space-y-2">
        {eventsLoading ? (
          <p className="text-center text-slate-400 py-6">Loading…</p>
        ) : events.items.map((e) => (
          <div key={e.id} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm" data-testid={`event-card-${e.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="min-w-0">
                <p className="font-medium text-slate-800 text-sm truncate">{[e.first_name, e.surname].filter(Boolean).join(' ') || e.user_email}</p>
                <p className="text-[11px] text-slate-500 truncate">{e.user_email}</p>
              </div>
              {e.success
                ? <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">OK</span>
                : <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-bold">{(e.reason || 'failed').replaceAll('_', ' ')}</span>}
            </div>
            <div className="flex items-center justify-between text-xs text-slate-600">
              <a href={ipMapUrl(e.ip)} target="_blank" rel="noreferrer" className="font-mono text-cyan-700">{e.ip || '—'}</a>
              <span>{e.country_code && <span className="mr-1">{flag(e.country_code)}</span>}{e.city || e.country || ''}</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1 font-mono">{fmtDate(e.created_at)}{e.device_type ? ` · ${e.device_type}` : ''}{e.browser ? ` · ${e.browser}` : ''}</p>
          </div>
        ))}
        {events.total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-xs bg-white rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-slate-600">Page {page + 1} of {Math.ceil(events.total / PAGE_SIZE)}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                data-testid="ip-mobile-prev"
                className="px-3 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-700 disabled:opacity-40">← Prev</button>
              <button onClick={() => setPage(page + 1)} disabled={(page + 1) * PAGE_SIZE >= events.total}
                data-testid="ip-mobile-next"
                className="px-3 py-1 bg-white border border-slate-300 rounded font-semibold text-slate-700 disabled:opacity-40">Next →</button>
            </div>
          </div>
        )}
      </div>
        </React.Fragment>
      )}
    </div>
  );
}
